/**
 * Chat Support Page
 * =================
 * صفحة الدردشة مع الدعم الفني - from database
 */

import React, { useState, useEffect, useRef } from 'react';
import { Card } from '../../../lib/ui/Card';
import { Button } from '../../../lib/ui/Button';
import { SearchBox } from '../../../lib/ui/SearchBox';
import { chatService } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { validateFiles, scanMessageText } from '../../../lib/security/fileSecurity';
import './ChatPage.css';

const URL_REGEX = /(https?:\/\/[^\s"'<>]+)/gi;
function renderText(msg) {
  if (!msg) return null;
  return msg.split(URL_REGEX).map((part, i) =>
    URL_REGEX.test(part)
      ? <a key={i} href={part} target="_blank" rel="noopener noreferrer" style={{ color:'#0ABAB5', textDecoration:'underline', wordBreak:'break-all' }}>{part}</a>
      : part
  );
}
function renderAttachments(atts, onPreview) {
  if (!atts || atts.length === 0) return null;
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'0.35rem', marginTop:'0.35rem' }}>
      {atts.map((att, i) => {
        if (att.type?.startsWith('image/')) return (
          <div key={i} style={{ position:'relative', cursor:'zoom-in', display:'inline-block' }} onClick={() => onPreview(att)}>
            <img src={att.data_url} alt={att.name} style={{ maxWidth:200, maxHeight:160, borderRadius:'0.5rem', objectFit:'cover', display:'block', border:'1px solid rgba(0,0,0,0.1)' }} />
            <div style={{ position:'absolute', bottom:4, left:4, background:'rgba(0,0,0,0.45)', borderRadius:4, padding:'1px 5px', fontSize:'0.55rem', color:'#fff' }}>🔍</div>
          </div>
        );
        return (
          <div key={i} style={{ display:'flex', alignItems:'center', gap:'0.4rem', background:'rgba(0,0,0,0.06)', borderRadius:'0.45rem', padding:'0.3rem 0.5rem', cursor:'pointer' }} onClick={() => onPreview(att)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{width:16,height:16,flexShrink:0,opacity:0.6}}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
            <span style={{ flex:1, fontSize:'0.75rem', fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:130 }}>{att.name}</span>
            <a href={att.data_url} download={att.name} title="تحميل" onClick={e => e.stopPropagation()} style={{ flexShrink:0, color:'#0ABAB5', lineHeight:1, display:'flex' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="14" height="14"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            </a>
          </div>
        );
      })}
    </div>
  );
}

const Icons = {
  chat: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
  send: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>,
  x: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  check: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>,
  user: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
  clock: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
};

const ChatPage = () => {
  const toast = useToast();
  const messagesEndRef = useRef(null);
  const [lightbox, setLightbox] = useState(null);

  const [loading, setLoading]           = useState(true);
  const [sessions, setSessions]         = useState([]);
  const [stats, setStats]               = useState({});
  const [selectedSession, setSelectedSession] = useState(null);
  const [searchQuery, setSearchQuery]   = useState('');
  const [newMessage, setNewMessage]     = useState('');
  const [sending, setSending]           = useState(false);
  const selectedIdRef                   = useRef(null);
  const inputRef                        = useRef(null);

  useEffect(() => { loadData(); }, []);

  // Polling every 4s
  useEffect(() => {
    const poll = async () => {
      if (document.hidden) return;
      try {
        const [sessionsData, statsData] = await Promise.all([
          chatService.getSessions().catch(() => ({ items: [] })),
          chatService.getStats().catch(() => ({})),
        ]);
        setSessions(sessionsData.items || []);
        setStats(statsData);
        // Refresh selected session messages silently
        if (selectedIdRef.current) {
          const updated = await chatService.getSession(selectedIdRef.current).catch(() => null);
          if (updated) setSelectedSession(prev => prev?.id === updated.id ? updated : prev);
        }
      } catch {}
    };
    const id = setInterval(poll, 4000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [selectedSession?.messages?.length]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [sessionsData, statsData] = await Promise.all([
        chatService.getSessions().catch(() => ({ items: [] })),
        chatService.getStats().catch(() => ({})),
      ]);
      setSessions(sessionsData.items || []);
      setStats(statsData);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSession = async (sessionId) => {
    try {
      const session = await chatService.getSession(sessionId);
      setSelectedSession(session);
      selectedIdRef.current = sessionId;
      setSessions(prev => prev.map(s => s.id === sessionId ? { ...s, unread_count: 0 } : s));
      setTimeout(() => inputRef.current?.focus(), 100);
    } catch (error) {
      toast.error('فشل في تحميل المحادثة');
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedSession) return;
    const textCheck = scanMessageText(newMessage);
    if (!textCheck.safe) { toast.error(textCheck.reason); return; }
    setSending(true);
    const msg = newMessage;
    setNewMessage('');
    try {
      await chatService.sendMessage(selectedSession.id, { message: msg, sender_type: 'admin' });
      const updated = await chatService.getSession(selectedSession.id);
      setSelectedSession(updated);
    } catch (error) {
      toast.error('فشل في إرسال الرسالة');
      setNewMessage(msg);
    } finally {
      setSending(false);
    }
  };

  const handleCloseSession = async () => {
    if (!selectedSession) return;
    try {
      await chatService.closeSession(selectedSession.id);
      toast.success('تم إغلاق المحادثة');
      setSelectedSession(null);
      selectedIdRef.current = null;
      loadData();
    } catch (error) {
      toast.error('فشل في إغلاق المحادثة');
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('ar-SA', { month: 'short', day: 'numeric' });
  };

  const filteredSessions = sessions.filter(s => 
    s.subject?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="watrly-chat">
      {/* File Preview Modal */}
      {lightbox && (
        <div onClick={() => setLightbox(null)} style={{ position:'fixed', inset:0, zIndex:99999, background:'rgba(0,0,0,0.92)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', width:'min(94vw,900px)', padding:'0.5rem 0.75rem', background:'rgba(255,255,255,0.08)', borderRadius:'0.75rem 0.75rem 0 0', gap:'0.5rem' }}>
            <span style={{ color:'#fff', fontSize:'0.85rem', fontWeight:700, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flex:1 }}>{lightbox.name}</span>
            <a href={lightbox.data_url} download={lightbox.name} style={{ color:'#0ABAB5', display:'flex', alignItems:'center', gap:'0.3rem', fontSize:'0.8rem', fontWeight:700, textDecoration:'none', flexShrink:0 }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="15" height="15"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              تحميل
            </a>
            <button onClick={() => setLightbox(null)} style={{ background:'rgba(255,255,255,0.12)', border:'none', borderRadius:'50%', width:32, height:32, color:'#fff', fontSize:'1rem', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>✕</button>
          </div>
          <div onClick={e => e.stopPropagation()} style={{ width:'min(94vw,900px)', background:'#111', borderRadius:'0 0 0.75rem 0.75rem', overflow:'hidden', maxHeight:'82vh', display:'flex', alignItems:'center', justifyContent:'center' }}>
            {lightbox.type?.startsWith('image/')
              ? <img src={lightbox.data_url} alt={lightbox.name} style={{ maxWidth:'100%', maxHeight:'82vh', objectFit:'contain', display:'block' }} />
              : <iframe src={lightbox.data_url} title={lightbox.name} style={{ width:'100%', height:'78vh', border:'none', display:'block' }} />
            }
          </div>
        </div>
      )}
      {/* Sidebar */}
      <div className="watrly-chat__sidebar">
        <div className="watrly-chat__sidebar-header">
          <h2>المحادثات</h2>
          <div className="watrly-chat__stats-mini">
            <span className="watrly-chat__stat-badge watrly-chat__stat-badge--active">{stats.active || 0}</span>
            <span className="watrly-chat__stat-badge watrly-chat__stat-badge--unread">{stats.unread_messages || 0}</span>
          </div>
        </div>
        
        <SearchBox 
          value={searchQuery} 
          onChange={setSearchQuery} 
          placeholder="بحث..." 
          className="watrly-chat__search"
        />

        <div className="watrly-chat__sessions">
          {loading ? (
            <div className="watrly-chat__loading">جاري التحميل...</div>
          ) : filteredSessions.length === 0 ? (
            <div className="watrly-chat__empty">لا توجد محادثات</div>
          ) : (
            filteredSessions.map(session => (
              <div 
                key={session.id}
                className={`watrly-chat__session-item ${selectedSession?.id === session.id ? 'watrly-chat__session-item--active' : ''} ${session.unread_count > 0 ? 'watrly-chat__session-item--unread' : ''}`}
                onClick={() => loadSession(session.id)}
              >
                <div className="watrly-chat__session-avatar">
                  {Icons.user}
                </div>
                <div className="watrly-chat__session-info">
                  <div className="watrly-chat__session-header">
                    <span className="watrly-chat__session-name">{session.customer_name || session.subject || 'عميل'}</span>
                    <span className="watrly-chat__session-time">{formatDate(session.last_message_at)}</span>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:'0.4rem' }}>
                    {session.status === 'waiting' && (
                      <span style={{ fontSize:'0.6rem', background:'rgba(245,158,11,0.15)', color:'#f59e0b', border:'1px solid rgba(245,158,11,0.3)', borderRadius:'0.4rem', padding:'0.05rem 0.35rem', fontWeight:700 }}>انتظار</span>
                    )}
                    <div className="watrly-chat__session-preview">
                      {session.last_message?.substring(0, 35)}{session.last_message?.length > 35 ? '...' : ''}
                    </div>
                  </div>
                </div>
                {session.unread_count > 0 && (
                  <span className="watrly-chat__unread-badge">{session.unread_count}</span>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="watrly-chat__main">
        {selectedSession ? (
          <>
            <div className="watrly-chat__header">
              <div className="watrly-chat__header-info">
                <div className="watrly-chat__header-avatar">{Icons.user}</div>
                <div>
                  <h3 style={{ margin:0, fontSize:'0.95rem' }}>{selectedSession.customer_name || selectedSession.subject || 'عميل'}</h3>
                  {selectedSession.customer_phone && (
                    <div style={{ fontSize:'0.7rem', color:'#0ABAB5', direction:'ltr', marginBottom:'0.15rem' }}>{selectedSession.customer_phone}</div>
                  )}
                  <span className={`watrly-chat__status watrly-chat__status--${selectedSession.status}`}>
                    {selectedSession.status === 'active' ? 'نشط' : selectedSession.status === 'waiting' ? 'في الانتظار' : 'مغلق'}
                  </span>
                </div>
              </div>
              <div className="watrly-chat__header-actions">
                {selectedSession.status === 'waiting' && (
                  <Button variant="primary" size="sm" onClick={async () => {
                    try {
                      await chatService.assignSession(selectedSession.id, 'admin');
                      const updated = await chatService.getSession(selectedSession.id);
                      setSelectedSession(updated);
                      loadData();
                    } catch { toast.error('فشل في قبول المحادثة'); }
                  }}>
                    قبول المحادثة
                  </Button>
                )}
                {(selectedSession.status === 'active' || selectedSession.status === 'waiting') && (
                  <Button variant="ghost" size="sm" onClick={handleCloseSession}>
                    {Icons.check} إغلاق
                  </Button>
                )}
              </div>
            </div>

            <div className="watrly-chat__messages">
              {selectedSession.messages?.length === 0 ? (
                <div className="watrly-chat__no-messages">لا توجد رسائل بعد</div>
              ) : (
                selectedSession.messages?.map((msg, idx) => {
                  const isAdmin = ['admin','agent'].includes(msg.sender_type);
                  const atts = msg.attachments || [];
                  return (
                    <div
                      key={idx}
                      className={`watrly-chat__message watrly-chat__message--${msg.sender_type}`}
                      style={{ display:'flex', justifyContent: isAdmin ? 'flex-start' : 'flex-end' }}
                    >
                      <div className="watrly-chat__message-content" style={{
                        background: isAdmin ? 'linear-gradient(135deg,#0ABAB5,#0891b2)' : undefined,
                        color: isAdmin ? '#fff' : undefined,
                        maxWidth: '72%',
                      }}>
                        {!isAdmin && <div style={{ fontSize:'0.65rem', color:'#0ABAB5', fontWeight:700, marginBottom:'0.1rem' }}>{selectedSession.customer_name || 'العميل'}</div>}
                        {msg.message && <p style={{ margin:0, whiteSpace:'pre-wrap' }}>{renderText(msg.message)}</p>}
                        {renderAttachments(atts, att => setLightbox(att))}
                        <span className="watrly-chat__message-time">{formatTime(msg.created_at)}</span>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {(selectedSession.status === 'active' || selectedSession.status === 'waiting') && (
              <div className="watrly-chat__input-area">
                <input
                  ref={inputRef}
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
                  placeholder="اكتب ردك على العميل..."
                  disabled={sending}
                />
                <Button 
                  variant="primary" 
                  onClick={handleSendMessage} 
                  loading={sending}
                  disabled={!newMessage.trim()}
                >
                  {Icons.send}
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="watrly-chat__placeholder">
            <div className="watrly-chat__placeholder-icon">{Icons.chat}</div>
            <h3>اختر محادثة للبدء</h3>
            <p>حدد محادثة من القائمة للرد على العملاء</p>
          </div>
        )}
      </div>

      {/* Stats Panel */}
      <div className="watrly-chat__stats-panel">
        <h3>إحصائيات الدردشة</h3>
        <div className="watrly-chat__stats-grid">
          <div className="watrly-chat__stat-card">
            <span className="watrly-chat__stat-value">{stats.total || 0}</span>
            <span className="watrly-chat__stat-label">إجمالي المحادثات</span>
          </div>
          <div className="watrly-chat__stat-card watrly-chat__stat-card--active">
            <span className="watrly-chat__stat-value">{stats.active || 0}</span>
            <span className="watrly-chat__stat-label">نشطة</span>
          </div>
          <div className="watrly-chat__stat-card watrly-chat__stat-card--waiting">
            <span className="watrly-chat__stat-value">{stats.waiting || 0}</span>
            <span className="watrly-chat__stat-label">في الانتظار</span>
          </div>
          <div className="watrly-chat__stat-card watrly-chat__stat-card--closed">
            <span className="watrly-chat__stat-value">{stats.closed || 0}</span>
            <span className="watrly-chat__stat-label">مغلقة</span>
          </div>
        </div>
        <p className="watrly-chat__stats-note">البيانات من قاعدة البيانات</p>
      </div>
    </div>
  );
};

export default ChatPage;
